import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Stack
} from '@mui/material';
import { Speed as SpeedIcon } from '@mui/icons-material';
import { severityApi } from '../../services/api';

const SeveritySchemeDialog = ({ 
  open, 
  onClose, 
  mode = 'create', // 'create' or 'edit'
  assetTypeId,
  scheme = null,
  userData,
  onUpdate 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    asset_type_id: assetTypeId || '',
    organization_id: userData?.organizationId || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && scheme) {
        setFormData({
          name: scheme.name || '',
          description: scheme.description || '',
          asset_type_id: scheme.asset_type_id || assetTypeId || '',
          organization_id: scheme.organization_id || userData?.organizationId || ''
        });
      } else {
        setFormData({
          name: '',
          description: '',
          asset_type_id: assetTypeId || '',
          organization_id: userData?.organizationId || ''
        });
      }
      setError(null);
    }
  }, [open, mode, scheme, assetTypeId, userData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Severity scheme name is required');
      return;
    }

    if (!formData.asset_type_id) {
      setError('Asset type is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (mode === 'edit' && scheme) {
        await severityApi.updateScheme(scheme.id, formData);
      } else {
        await severityApi.createScheme(formData);
      }

      // Notify parent component to refresh data
      if (onUpdate) {
        await onUpdate();
      }

      // Close dialog
      onClose();
    } catch (err) {
      console.error('Error saving severity scheme:', err);
      setError(err.response?.data?.error || 'Failed to save severity scheme');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!scheme || mode !== 'edit') return;

    if (!window.confirm(`Are you sure you want to delete the severity scheme "${scheme.name}"? This will also delete all associated severity levels.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await severityApi.deleteScheme(scheme.id);

      // Notify parent component to refresh data
      if (onUpdate) {
        await onUpdate();
      }

      // Close dialog
      onClose();
    } catch (err) {
      console.error('Error deleting severity scheme:', err);
      setError(err.response?.data?.error || 'Failed to delete severity scheme');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', display: 'flex', alignItems: 'center', gap: 1 }}>
        <SpeedIcon />
        {mode === 'create' ? 'Create' : 'Edit'} Severity Scheme
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
            
            <TextField
              label="Severity Scheme Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              fullWidth
              placeholder="e.g., Road Infrastructure Severity, Bridge Assessment Scale"
              helperText="Enter a descriptive name for this severity scheme"
              disabled={loading}
            />
          </Box>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Describe the severity levels and criteria for this scheme..."
            helperText="Optional: Provide details about the severity classification system"
            disabled={loading}
          />

          {/* Preview information */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Name:</strong> {formData.name || 'Not specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Organization:</strong> {userData?.organizationName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Asset Type ID:</strong> {formData.asset_type_id}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Note: Each organization can have only one severity scheme per asset type.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        {mode === 'edit' && (
          <Button
            onClick={handleDelete}
            color="error"
            disabled={loading}
            sx={{ mr: 'auto' }}
          >
            Delete Scheme
          </Button>
        )}
        
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          color="warning"
          sx={{ px: 3 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            mode === 'create' ? 'Create Severity Scheme' : 'Update Severity Scheme'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeveritySchemeDialog; 